import React, { useMemo, useState } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";

import { isAccountExists } from "@/api/core/utils/accounts";
import { useAccountSearch, SearchTypes } from "@/api/search";
import Combobox from "@/components/combobox/combobox";
import { useEffectAfterMount, useRecentSearches } from "@/hooks";
import { accountSearchHistory } from "@/settings";

import { SearchResultRow, SearchFooterRow } from "./search-results";

import type { Account as AccountEntity } from "@/schemas";

interface SearchProps {
  autoFocus?: boolean;
  autoSubmit?: boolean;
  onFocus?(): void;
  showSearchHistory?: boolean;
}

const Search = (props: SearchProps) => {
  const { autoFocus, autoSubmit, showSearchHistory = false, onFocus } = props;
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const isUsingSearchPage = Boolean(location.pathname.match(/^\/search/));
  const queryParam = searchParams.get("q") ?? "";

  const {
    value,
    setValue,
    debouncedValue,
    recentSearches,
    refreshRecentSearches,
    commitSearch,
    clearRecentSearch,
    clearRecentSearches,
  } = useRecentSearches(accountSearchHistory, {
    initialValue: isUsingSearchPage ? queryParam : "",
  });

  const [isFocused, setIsFocused] = useState(false);

  const { isFetching, isFetched, entities } = useAccountSearch(debouncedValue, {
    isEnabled: !autoSubmit && isFocused,
  });

  const navigateToSearchResults = (term = value) => {
    // Stay on the tab the search page is already showing; a search started
    // anywhere else lands on profiles.
    const tab = isUsingSearchPage
      ? searchParams.get("type")
      : SearchTypes.ACCOUNTS;

    const params = new URLSearchParams({ q: term });
    if (tab) {
      params.set("type", tab);
    }

    navigate(`/search?${params}`);
  };

  const handleSelect = (item: AccountEntity | null) => {
    const selected = item ? `@${item.acct}` : value;

    if (selected.startsWith("@") && isAccountExists(selected)) {
      commitSearch(selected.slice(1));
      navigate(`/${selected}`);
      setValue("");
    } else {
      commitSearch(selected);
      navigateToSearchResults(selected);
    }
  };

  useEffectAfterMount(() => {
    if (autoSubmit) {
      navigateToSearchResults();
    }
  }, [debouncedValue]);

  // The search page is driven by `?q=`, which a search started elsewhere (the
  // sidebar) can change while this input is already mounted. Skip the value
  // this input just submitted itself.
  useEffectAfterMount(() => {
    if (isUsingSearchPage && queryParam !== debouncedValue) {
      setValue(queryParam);
    }
  }, [queryParam]);

  const recentSearchItems = useMemo(
    () => (showSearchHistory ? recentSearches.slice(0, 4) : []),
    [showSearchHistory, recentSearches],
  );
  const isQueryOnly = autoSubmit || isUsingSearchPage;

  return (
    <Combobox
      autoFocus={autoFocus}
      label="search"
      value={value}
      onValueChange={setValue}
      onFocus={() => {
        // Another mounted search may have committed a term since this one read
        // the history.
        refreshRecentSearches();

        if (!isQueryOnly) {
          setIsFocused(true);
        }

        onFocus?.();
      }}
      onBlur={isQueryOnly ? undefined : () => setIsFocused(false)}
      recentSearches={{
        items: recentSearchItems,
        onRemove: clearRecentSearch,
        onClearAll: clearRecentSearches,
      }}
      // `autoSubmit` already navigates on every keystroke, so Enter has
      // nothing left to commit.
      onEnterWithoutSelection={
        autoSubmit ? undefined : () => handleSelect(null)
      }
      results={
        isQueryOnly
          ? undefined
          : {
              data: entities,
              isLoading: isFetching,
              isLoaded: isFetched,
              limit: -1,
              itemContent: (account) => <SearchResultRow account={account} />,
              itemTestId: (account) => `search-account-result-${account.acct}`,
              onSelect: handleSelect,
            }
      }
      footerItem={
        isQueryOnly
          ? undefined
          : {
              content: (query) => <SearchFooterRow query={query} />,
              testId: "other-result",
              onSelect: navigateToSearchResults,
            }
      }
    />
  );
};

export default Search;
